import type { ReactNode } from 'react'
import type { TapeItem } from '@/lib/market/types'

import Link from 'next/link'
import { cn } from '@/lib/utils'
import { formatPrice } from '@/lib/market/format'
import { ChangeText, DirectionIcon } from '@/components/market/change'
import { useQuote, usePriceFlash } from '@/components/market/live-provider'

const TapeEntry = ({ item, hidden }: { item: TapeItem, hidden?: boolean }): ReactNode => {
    const quote = useQuote(item.live)
    const flash = usePriceFlash(quote.last)

    return (
        <Link
            href={`/stocks/${item.slug}`}
            tabIndex={hidden ? -1 : undefined}
            className={cn('flex items-center gap-2 whitespace-nowrap rounded px-3 py-1 text-xs transition-colors hover:bg-secondary', flash)}
        >
            <span className='font-mono font-semibold tracking-tight text-foreground'>{item.ticker}</span>
            <span className='tabular-nums text-foreground/85'>{formatPrice(quote.last)}</span>
            <span className={cn('flex items-center', quote.changePct > 0 ? 'text-up' : quote.changePct < 0 ? 'text-down' : 'text-muted-foreground')}>
                <DirectionIcon value={quote.changePct} className='size-3' />
            </span>
            <ChangeText value={quote.changePct} />
        </Link>
    )
}

const TickerTape = ({ items }: { items: TapeItem[] }): ReactNode => {
    if (!items.length) return null

    return (
        <div className='ticker-wrap relative border-b border-border bg-card/60 motion-reduce:overflow-x-auto'>
            <div className='pointer-events-none absolute inset-y-0 left-0 z-10 w-10 bg-linear-to-r from-background to-transparent' aria-hidden='true' />
            <div className='pointer-events-none absolute inset-y-0 right-0 z-10 w-10 bg-linear-to-l from-background to-transparent' aria-hidden='true' />

            <div className='overflow-hidden motion-reduce:overflow-visible'>
                <div className='ticker-track flex w-max items-center py-1' style={{ animationDuration: `${Math.max(60, items.length * 4.2)}s` }}>
                    <nav aria-label='Most traded shares' className='flex items-center'>
                        {items.map(item => <TapeEntry key={item.id} item={item} />)}
                    </nav>

                    <div aria-hidden='true' className='flex items-center motion-reduce:hidden'>
                        {items.map(item => <TapeEntry key={`${item.id}-copy`} item={item} hidden />)}
                    </div>
                </div>
            </div>
        </div>
    )
}

export default TickerTape
